import { React, useEffect, useState, createContext, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import { AgGridReact } from "ag-grid-react"; //, the AG Grid React Component
import "ag-grid-community/styles/ag-grid.css"; // Core grid CSS, always needed
import "ag-grid-community/styles/ag-theme-alpine.css"; // Optional theme CSS
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getAuthToken } from "../api/api";
import Release from "../bootstrap/Release";
import {CustomConfirmationComponent} from '../components/common/ConfirmationComponent';
import Tabs from '../components/common/TabComponent';
import { getAPIUrl, notify, suppressKeyboardEvent } from "../components/common/helpers";
import { getUserDetails } from "../components/sidebar/Sidenav";
import RejectionSelectionModal from "../bootstrap/ReasonModal";
import RejectionDisplayModal from "../bootstrap/ReasonDisplayModal";
import { ReleaseCustomCellRenderer } from "../components/common/CustomCellRenderer";
import CustomDetailsComponent from "../components/common/CustomDetailsComponent";
import "../styles/Modal.css";

import styled from "styled-components";

const StyledReleaseDetails = styled.div`
  .btn:disabled {
    cursor: not-allowed;
    pointer-events: auto;
  }
  .Deletebtn {
    background: transparent;
    border: none;
    color: white;
  }
  .Deletebtn:hover {
    background: transparent;
    border: none;
  }
  .Deletebtn:disabled {
    cursor: not-allowed;
    pointer-events: auto;
  }
`;

const ReleaseContext = createContext();
export default function ReleaseManagement(props) {
  const { disabledFeatures } = props;
  const [isRejected, setIsRejected] = useState(false);
  const [gridEntries, setGridEntries] = useState();
  const [moreReleaseInfo, setMoreReleaseInfo] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const [isDelete, setIsDelete] = useState(false);
  const [field, setField] = useState();
  const [gridApi, setGridApi] = useState(null);
  const [status, setStatus] = useState('All Releases');
  const [fileText, setFileText] = useState(null);
  const [title, setTitle] = useState("");
  const gridRef = useRef();

  const controller = new AbortController();

  const onGridReady = (params) => {
    setGridApi(params.api);
  };

  useEffect(() => {
    document.title = "Release Management";
    if(gridApi) {
      getRelease();
    }
    return () => {
      controller.abort();
    };
   }, [gridApi, status]);

   const createServerRequest = (params) => {
    const {startRow, endRow, filterModel, sortModel} = params;
    let sortQuery = '';
    let filterParams = '';
    let statusQuery = '';
    if (sortModel && sortModel.length > 0 && sortModel !== undefined) {
      sortQuery = new URLSearchParams({
          sortColumn: sortModel[0].colId,
          sortOrder: sortModel[0].sort,
        });
    }
    if (Object.keys(filterModel).length > 0) {
     filterParams = Object.keys(filterModel).map(field => {
       const filter = filterModel[field];
       const params = new URLSearchParams({
              [field]: [filter.filter],
            });
          return `&${params}`
     }).join('');
    }
    if (status !== "All Releases") {
      statusQuery = new URLSearchParams({approval_state : status});
    }
    const finalQuery = `?filter_page=${startRow}&filter_pageSize=${endRow}${filterParams.toString()}&${statusQuery.toString()}`;
       if(sortQuery !== '') {
        return finalQuery + `&${sortQuery.toString()}`;
       } else {
        return finalQuery;
        }
}

const showErrorOverlay = (gridApi) => {
  if (gridApi) {
    gridApi.showNoRowsOverlay();
  }
};

const handleApiError = (gridApi) => {
    showErrorOverlay(gridApi);
};

  const getRejectionReason = (field) => {
    setIsRejected(false);
    setReadOnly(true);
    setGridEntries(field);
    setMoreReleaseInfo(false);
  };

  const handleReleaseDetails = (field) => {
    setReadOnly(true);
    setIsDelete(false);
    setGridEntries(field);
    setMoreReleaseInfo(true);
  }

  const handleDeleteRelease = (field) => {
    setIsDelete(true);
    setField(field);
  };

  const handleRejectClick = (field) => {
    const release_details_id = field.release_details_id;
    const confirmReject = window.confirm(
      "Are you sure, you want to reject this Release",
      release_details_id
    );
    if (confirmReject) {
      setIsRejected(true);
      setReadOnly(false);
      setGridEntries(field);
    }
  }

  const handleApproveClick = (field) => {
    const release_details_id = field.release_details_id;
    const confirm = window.confirm(
            "Are you sure, you want to approve this Release",
            release_details_id
          );
      if (confirm) {
    const body = JSON.stringify({
      release_details_id,
      approval_state: "approved",
      updated_by: getUserDetails().username,
    });
    fetch(getAPIUrl("approveAndRejectApi"), {
      headers: { Authorization: getAuthToken() },
      method: "POST",
      body,
    }).then((resp) => {
      resp.json();
      getRelease();
    })
    .catch((error) => {
      error.message !== "canceled" && notify("approve/reject");
    })
  }
  }

  const handleDownloadReleaseNotes = async (field) => {
    const {file_name, component_type_id, firmware_version} = field;
    const params = new URLSearchParams({
        file_name: file_name,
        component_type_id: component_type_id,
        firmware_version: firmware_version,
      });
      
    try {
      axios({
        method: "GET",
        url: `${getAPIUrl("releaseNoteDownload")}?${params.toString()}`,
        headers: { Authorization: getAuthToken() },
        signal: controller.signal,
      })
      .then(async (resp) => {
        if (resp.status === 200) {
            const fileLink = resp.data;
          
            try {
              const url = new URL(fileLink);
              const fileResponse = await fetch(url.toString());
          const fileText = await fileResponse.text();
                  setFileText(fileText);
                  setTitle(`Release Notes of Firmware - ${field.firmware_name} of Firmware Version - ${field.firmware_version}`);
            } catch (err) {
              console.error('Invalid or untrusted URL:', err);
            }
          }
      })
      .catch((err) => {
        err.message !== "canceled" && notify("downloading");
      });
    } catch (error) {
      console.log(error);
     notify("downloading");
    }
  }

  const column = [
    {
      headerName: "Firmware Version",
      field: "firmware_version",
      floatingFilter: true,
      flex: 1,
    },
    {
      headerName: "Site",
      field: "building_name",
      floatingFilter: true,
      width: 100,
    },
    {
      headerName: "Component Type",
      field: "component_type",
      floatingFilter: true,
      flex: 1,
    },
    {
      headerName: "Approval State",
      field: "approval_state",
      flex: 1,
      hide: status === "Pending" || status === "pending",
      colId: "action",
      cellRenderer: ReleaseCustomCellRenderer,
      cellRendererParams: {
       type : "approvalState",
       getRejectionReason
      }
    },
    {
      headerName: "Download Status",
      field: "download_status",
      floatingFilter: true,
      flex: 1,
    },
    {
      headerName: "Created Date",
      field: "created_date",
      floatingFilter: true,
      flex: 1,
    },
    {
      headerName: "Approved/Rejected Date",
      field: "approvedOrRejectedDate",
      floatingFilter: true,
      hide: status === "Pending" || status === "pending",
      flex: 1,
    },
    {
      headerName: "Report",
      field: "file_id",
      maxWidth:100,
      minWidth: 100,
      colId: "action",
      cellRenderer: ReleaseCustomCellRenderer,
      cellRendererParams: {
       type : "viewmore"
      }
    },
    {
      headerName: "Release Notes",
      colId: "release_notes",
      flex: 1,
      minWidth: 100,
      cellRenderer: ReleaseCustomCellRenderer,
      cellRendererParams: {
       type : "release_notes",
       handleDownloadReleaseNotes
      }
    },
    {
      headerName: "Details",
      field: "release_details_id",
      maxWidth: 100,
      colId: "details",
      minWidth: 100,
      cellRenderer: ReleaseCustomCellRenderer,
      cellRendererParams: {
       type : "details",
       handleReleaseDetails
      }
    },
    {
      headerName: "Action",
      hide: disabledFeatures.includes("AddRelease"),
      field: "release_details_id",
      width: 250,
      colId: "action",
      minWidth: (status === "Pending" || status === "pending" || status === "All Releases") ? 250 : 150,
      cellRenderer: ReleaseCustomCellRenderer,
      cellRendererParams: {
       type : "approvals",
       status: status,
       disabledFeatures: disabledFeatures,
       handleDeleteRelease,
       handleApproveClick,
       handleRejectClick,
      }
    },
  ];
  const defaultColDef = {
    sortable: true,
    filter: true,
    flex: 1,
    resizable: true,
    minWidth: 110,
    suppressKeyboardEvent
  };

  const requiredKeys = {
    firmware_version: "Firmware Version",
    building_name: "Site", 
    component_type: "Component Type",
    };

  const handleTabSelection = (active) => {
    setIsDelete(false);
    setStatus(active);
  }

  const getRelease = useCallback(async () => {
    if(!gridApi) return;
    const dataSource = {
      getRows: (params) => {
        const requestParameters = createServerRequest(params);
        gridApi.showLoadingOverlay();
        axios({
                    method: "GET",
                    url:
                      getAPIUrl("getReleaseDetailsApi") + requestParameters,
                    headers: {
                      Authorization: getAuthToken(),
                    },
                    signal: controller.signal,
                  })
                    .then((res) => {
                        if (res && res.data.releaseDetails.length > 0) {
                          const responseData = res.data.releaseDetails.map((item) => ({
                            ...item
                          }));
                          params.successCallback(responseData, res.data.lastRow);
                          gridApi.hideOverlay();
                        }
                      else {
                        params.successCallback([], 0);
                        handleApiError(gridApi);
                      }
                    })
                    .catch((err) => {
                      params.successCallback([],  0);
                      params.failCallback();
                      handleApiError(gridApi);
                      (err.message !== "canceled" && err.messageDescription === "No Record Found") && notify("fetching");
                    });
      }
    }
    gridApi.setDatasource(dataSource);
  }, [controller.signal, status]);
  

    const handleConfirm = () => {
      setIsDelete(false);
      const { release_details_id } = field;
      const body = JSON.stringify({
            release_details_id,
            title: "ReleaseManagement",
            updated_by: getUserDetails().username,
          });
    
          axios({
            method: "PATCH",
            url: getAPIUrl("deleteFunctionApi"),
            headers: { Authorization: getAuthToken()},
            signal: controller.signal,
            data: body
          })
            .then((resp) => {
              if(resp){
                getRelease();
              }
            })
            .catch((error) => {
              console.log(error);
            });
    }
  
  const contextProps = useMemo(() => ({
      isRejected,
      setIsRejected,
      gridEntries,
      readOnly,
      setReadOnly,
      getRelease,
      moreReleaseInfo,
  }), [gridEntries, getRelease, isRejected, moreReleaseInfo, readOnly])
   
  return (
    <StyledReleaseDetails>
      <h4 className="text-white p-2">Release Management</h4>
      <div
        className="container-fluid"
        style={{
          background: "#1F1F1F 0% 0% no-repeat padding-box",
          border: "1px solid #707070",
          width: "100%",
        }}
      >
        <div className="text-end py-2">
          <Release
            disabledFeatures={disabledFeatures}
            getRelease={getRelease}
          />
        </div>
        <div className="text-end" >
        <Tabs onTabSelection={handleTabSelection}/>
        </div>
          <ReleaseContext.Provider
            value={contextProps}
          >
            {(readOnly && !isDelete)  ? <RejectionDisplayModal /> : <RejectionSelectionModal />}
            {fileText !== null && <CustomDetailsComponent
            fileText = {fileText}  
            setFileText = {setFileText}
            setReadOnly={setReadOnly}
            title ={title}
              /> }
            {(isDelete && !readOnly && !isRejected) && 
           <CustomConfirmationComponent 
            fieldData = {field} 
            onConfirm={handleConfirm} 
            setIsDelete={setIsDelete}
            Datadisplay= {requiredKeys}
            message="Are you sure, you want to delete this Release?"
            />
            }
          </ReleaseContext.Provider>
        <div
          className="ag-theme-alpine-dark"
          style={{ height: 545, width: "100%" }}
        >
          <AgGridReact
            onGridReady={onGridReady}
            ref={gridRef}
            rowModelType={"infinite"}
            columnDefs={column}
            defaultColDef={defaultColDef}
            pagination={true}
            paginationPageSize={10}
            cacheBlockSize={10}
            filter= {true}
            sortable={true}
          ></AgGridReact>
        </div>

        <div className="py-2"></div>
      </div>
      <ToastContainer
        style={{ width: "50%" }}
        closeOnClick={false}
        position="top-center"
        autoClose={5000}
        hideProgressBar={true}
        newestOnTop={false}
        limit={1}
      />
    </StyledReleaseDetails>
  );
}

export { ReleaseContext };
